/**
 * Assets factory.
 * Factory class for creation of Sprites, Text etc.
 */
import TextField, { ITextFieldStyleOptions } from "../views/components/TextField";
import App from "../App";
import Layout from "./Layout";
import * as Particles from "pixi-particles";
import Utils from "./Utils";
import { ConfigType } from "./ConfigType";
import { AssetConfigType } from "./AssetConfigType";
import DragonBonesAnimation from "../views/DragonBonesAnimation";
import Configs from "../../../src/vendorConfigs/Configs";
import SpineAnimation from '../views/SpineAnimation';
import UIController from "../ui/UIController";
import { Texture, Loader, Container, Sprite, TilingSprite, AnimatedSprite, NineSlicePlane, Rectangle } from "pixi.js";
import ConsoleUtil from "./ConsoleUtil";
import GameModel from "../model/GameModel";

class Assets {
    protected symAnimTextures: Texture[][];
    protected padding: number;
    private _loaded: boolean = false;
    public get loaded(): boolean {
        return this._loaded;
    }


    constructor() {
        this.symAnimTextures = [];
        this.padding = Layout.relWidth(App.config.tfPadding);
    }

    public get resources() {
        return Loader.shared.resources;
    }

    public getConfig(configType: AssetConfigType): ConfigType {
        return (this.resources[configType]?.data || {}) as ConfigType;
    }

    onResourcesLoaded() {
        this.init();
        this._loaded = true;

        // Preinitializing components
        UIController.preInitializeComponents();
    }

    public getParticleConfig( particleId: string) {
       return Utils.deepClone(this.resources.particlesConfig.data)[particleId].config;
    }

    getParticle(
        particleId: string,
        container: Container
    ): Particles.Emitter {
        let cfg = Utils.deepClone(this.resources.particlesConfig.data);
        if (!cfg[particleId]) {
            ConsoleUtil.log("Could not find particle: " + particleId);
            return;
        }
        cfg[particleId].config = this.rescaleParticleConfig(
            cfg[particleId].config
        );
        let emiter = new Particles.Emitter(
            container,
            GameModel.isMobile() ? cfg[particleId].mobileTextures || cfg[particleId].textures : cfg[particleId].textures,
            cfg[particleId].config
        );
        emiter.particleConstructor = Particles.AnimatedParticle;

        return emiter;
    }

    rescaleParticleConfig(cfg: any): any {
        let result: any = Utils.deepClone(cfg);

        for (let prop in result) {
            if (
                prop == "scale" ||
                prop == "acceleration" ||
                prop == "particleSpacing" ||
                prop == "speed"
            ) {
                if (!isNaN(result[prop])  ) {
                    result[prop] = Layout.scalePixels( result[prop]  )
                } else {
                    for ( let propPr in result[prop] ) {
                        if (!isNaN(result[prop][propPr])  ) {
                            result[prop][propPr] = Layout.scalePixels( result[prop][propPr]  )
                        }
                    }
                }
            }
        }

        return result;
    }

    /**
     * Return the array of animation textures for a symbol with specified id.
     * @public
     * @param symId {Number} - the id of the symbol
     * @return {Array} Array of Texture
     */
    getSymAnimTextures(symId: number): Texture[] {
        return this.symAnimTextures[symId];
    }

    /**
     * Return a texture with specified id. The id is based on the file name of the image file.
     * The texture atlases are searched consecutively until the image is found
     * The file suffix (.png) is added automatically;
     * Returns null if texture is not found
     * @public
     * @param textureId {String}
     * @return {Texture}
     */
    getTexture(textureId: string): Texture {
        let texture = null;
        for (let atlasName in this.resources) {
            let atlas = this.resources[atlasName];
            if (atlas.textures) {
                texture = atlas.textures[textureId];
            } else if (atlasName == textureId) {
                texture = atlas.texture;
            }
            if (texture != undefined) {
                break;
            }
        }

        // If the texture is not found and it's not _000 texture of
        // an animation that starts with 001 - show an error
        if (texture == null && textureId.indexOf("_000") == -1) {
            ConsoleUtil.log("Could not find texture : " + textureId);
        }

        return texture;
    }

    /**
     * Get a Sprite which uses texture with a specified id
     * @public
     * @param textureId {String}
     * @returns {Sprite}
     */
    getSprite(textureId: string): Sprite {
        return new Sprite(this.getTexture(textureId));
    }
    /**
     * Get a Sprite which uses texture with a specified id
     * @public
     * @param textureId {String}
     * @returns {TilingSprite}
     */
    getTilingSprite(
        textureId: string,
        width: number,
        height: number
    ): TilingSprite {
        return new TilingSprite(
            this.getTexture(textureId),
            width,
            height
        );
    }

    /**
     * Get a Sprite which uses texture from a specific layer with a specified id
     * @public
     * @param textureId {String}
     * @param layerName {String}
     * @param sceneName {String}
     * @returns {Sprite}
     */
    getLayerSprite(
        textureId: string,
        layerName: string,
        sceneName?: string
    ): Sprite {
        const texture = this.getLayerTexture(textureId, layerName, sceneName);
        return new Sprite(texture);
    }

    getLayerTexture(
        textureId: string,
        layerName: string,
        sceneName?: string
    ): Texture {
        let textureName =
            layerName + (sceneName ? "-" + sceneName : "") + "/" + textureId;
        let texture = this.getTexture(textureName);
        if (!texture) {
            textureName = layerName + "/" + textureId;
            texture = this.getTexture(textureName);
        }

        return texture;
    }

    /**
     * Factory method for creating text fields.
     * @public
     * @param text {String} - the text to be displayed
     * @param size {Number}
     * @param font {String}
     * @param color {String}
     * @param lineHeight {Number} - distance between lines
     * @param wrapWidth {Number} - text wrap width
     * @param additionalStyle {Object} - additional style variables
     * @returns {TextField}
     */
    getTF(
        text: string,
        size: number,
        font: string = null,
        color: string = null,
        lineHeight: number = null,
        wrapWidth: number = null,
        additionalStyle: any = null
    ) {
        return new TextField(
            text,
            this.getTextStyle(
                size,
                font,
                color,
                lineHeight,
                wrapWidth,
                additionalStyle
            ),
            { multistyle: false }
        );
    }

    getTextStyle(
        size: number,
        font: string = null,
        color: string = null,
        lineHeight: number = null,
        wrapWidth: number = null,
        additionalStyle: any = null
    ): ITextFieldStyleOptions {
        font = font == null ? Object.keys(App.config.fonts)[0] : font;
        color = color == null ? App.config.defaultFontColor : color;
        size = Math.round(size);

        const styleVars: any = {
            fontFamily: font,
            fontSize: size,
            fill: color,
            align: "center",
        };

        for (const stylePropName in additionalStyle) {
            styleVars[stylePropName] = additionalStyle[stylePropName];
        }

        if (lineHeight != null) {
            styleVars.lineHeight = Math.round(size * lineHeight);
        }

        if (wrapWidth != null) {
            styleVars.wordWrap = true;
            styleVars.wordWrapWidth = wrapWidth;
        }

        styleVars.padding = this.padding;
        delete styleVars.padding;

        return styleVars;
    }

    /**
     * Create dragon bones animation
     * @public
     */
    public getDragonBonesAnimation(animName: string,clipName?:string): DragonBonesAnimation {
        let dbAnim = new DragonBonesAnimation(clipName || animName,
            this.resources[animName + "Data"].data,
            this.resources[animName + "Textures"].data,
            this.resources[animName + "Image"].texture);

        return dbAnim;
    }
    /**
     * Create spine animation
     * @public
     */
    public getSpineAnimation(animName: string): SpineAnimation {
        const spineAnim = new SpineAnimation(
            this.resources[`spine~${animName}~json`].spineData,
            animName,
            Configs.spineAnimations.getAnimConfig(animName)
        );

        return spineAnim
    }

    /**
     * Initialize the assets controller
     * @private
     */
    init() {
        const numSyms = App.config.symbolIds.length;
        for (let symIdx = 0; symIdx < numSyms; symIdx++) {
            let symId: number | string = App.config.symbolIds[symIdx];
            let symName: number | string =
                App.config.symbolAssetsMap[symId].name;

            const symAnimData = App.config.animData[symName];

            if (symAnimData && (symAnimData.numFrames || symAnimData.fade)) {
                let frames;
                if (symAnimData.numFrames && symAnimData.animName) {
                    frames = this.generateFrames(
                        "sym_" + symAnimData.animName + "_",
                        3,
                        symAnimData.numFrames
                    );
                } else if (symAnimData.numFrames) {
                    frames = this.generateFrames(
                        "sym_" + symName + "_",
                        3,
                        symAnimData.numFrames
                    );
                } else if (symAnimData.fade) {
                    frames = [this.getTexture("sym" + symName + "_win")];
                }

                this.symAnimTextures[symId] = frames;
                let anim = new AnimatedSprite(frames);
                App.instance.renderer.plugins.prepare.upload(anim);
            }
        }
    }

    /**
     * Generates an array of textures.
     * @public
     * @param prefix {String} texture prefix
     * @param padLen {Number} number of symbols of padding of the numeric part (e.g. 001)
     * @param endNum {Number} the id of the last texture
     * @param reverse {boolean} if the frames should be generated in reverse
     * @return {Array} Array of Texture
     */
    generateFrames(
        prefix: string,
        padLen: number,
        endNum: number,
        reverse = false,
        suffix = ""
    ) {
        let frames = [];
        let frameNames = this.generateFrameNames(prefix, padLen, 0, endNum, suffix);

        for (let frameIdx = 0; frameIdx < frameNames.length; frameIdx++) {
            let texture = this.getTexture(frameNames[frameIdx]);
            texture && frames.push(texture);
        }

        return reverse ? frames.reverse() : frames;
    }

    /**
     * Generates the names of frames - helper for generateFrames
     * @public
     * @param prefix {String} texture prefix
     * @param padLen {Number} number of symbols of padding of the numeric part (e.g. 001)
     * @param startNum {Number} the id of the first texture
     * @param endNum {Number} the id of the last texture
     * @param suffix {String} texture suffix
     * @return {Array} Array of Texture
     */
    generateFrameNames(
        prefix: string,
        padLen: number,
        startNum: number,
        endNum: number,
        suffix: string = ""
    ) {
        let frameNames = [];
        for (let frameIdx = startNum; frameIdx <= endNum; frameIdx++) {
            let frameString = String(frameIdx);
            while (frameString.length < padLen) {
                frameString = "0" + frameString;
            }

            frameNames.push(prefix + frameString + suffix);
        }

        return frameNames;
    }

    /**
     * Add stroke to a text field
     * @param tf
     * @param strokeData
     */
    addTFStroke(tf: TextField, strokeData: any) {
        if (strokeData) {
            tf.style.stroke = strokeData.strokeColor;
            let size = Layout.relHeight(strokeData.strokeSize);
            tf.style.strokeThickness = size;
            tf.style.miterLimit = 1;
        }
    }

    /**
     * Get scale nine sprite with a cut defined by its id
     * @param texId
     */
    getS9Sprite(texId: string) {
        let texture = this.getTexture(texId);
        let rect = this.getS9Rect(texId);
        return new NineSlicePlane(
            texture,
            rect.x,
            rect.y,
            rect.width,
            rect.height
        );
    }

    getS9Rect(texId: string) {
        let r;
        switch (texId) {
            case "messageBg":
                r = new Rectangle(32, 32, 32, 32);
                break;
        }

        return r;
    }
}

export default Assets;
